/**
 * Pure, side-effect-free calendar helpers backing the availability picker.
 *
 * The availability calendar renders a month grid of dates and asks the API for
 * the AVAILABLE/UNAVAILABLE status of each visible day. Keeping the date math
 * here (month grid construction, ISO formatting, month stepping, range bounds)
 * pure means it can be unit-tested without a DOM and reused by both the
 * component and any later logic.
 *
 * All dates are treated as UTC calendar dates (YYYY-MM-DD) with no time zone or
 * time-of-day component, matching the shared `IsoDate` contract.
 */

import type { IsoDate } from "@photography-booking/shared";

/** Full month names, indexed 0 (January) … 11 (December). */
export const MONTH_NAMES: readonly string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Short weekday labels, Sunday-first. */
export const WEEKDAY_LABELS: readonly string[] = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

/** A year + zero-based month pair identifying a single calendar month. */
export interface YearMonth {
  /** Full year, e.g. 2025. */
  year: number;
  /** Zero-based month index: 0 = January … 11 = December. */
  month: number;
}

/** A single day cell in the rendered month grid. */
export interface CalendarCell {
  /** ISO calendar date for this cell, e.g. "2025-03-14". */
  iso: IsoDate;
  /** Day-of-month number, 1…31. */
  day: number;
  /** True when the cell belongs to the grid's focused month. */
  inMonth: boolean;
}

/** Pads a number to two digits, e.g. 3 -> "03". */
function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Formats a UTC year/month/day triple as an ISO calendar date. */
export function isoFromParts(year: number, month: number, day: number): IsoDate {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

/** Returns today's date as an ISO calendar date (UTC). */
export function todayIso(now: Date = new Date()): IsoDate {
  return now.toISOString().slice(0, 10);
}

/** Returns the {@link YearMonth} containing `iso` (defaults to today). */
export function currentYearMonth(iso: IsoDate = todayIso()): YearMonth {
  const [year, month] = iso.split("-").map(Number);
  return { year, month: month - 1 };
}

/**
 * Steps a {@link YearMonth} by `delta` months, normalizing year roll-over in
 * both directions (e.g. December + 1 -> next January, January - 1 -> prior
 * December).
 */
export function addMonths({ year, month }: YearMonth, delta: number): YearMonth {
  const absolute = year * 12 + month + delta;
  return {
    year: Math.floor(absolute / 12),
    month: ((absolute % 12) + 12) % 12,
  };
}

/** Number of days in the given zero-based month (handles leap years). */
export function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this month.
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/**
 * The inclusive ISO date range covering an entire month, used to ask the API
 * for the status of every day shown for that month.
 */
export function monthRange({ year, month }: YearMonth): {
  from: IsoDate;
  to: IsoDate;
} {
  return {
    from: isoFromParts(year, month, 1),
    to: isoFromParts(year, month, daysInMonth(year, month)),
  };
}

/**
 * Builds the day cells for a month grid, Sunday-first.
 *
 * The grid is padded with the trailing days of the previous month and the
 * leading days of the next month so it always forms complete weeks (rows of
 * seven). Cells from adjacent months carry `inMonth: false` so the component
 * can render them muted/non-interactive.
 */
export function buildMonthGrid({ year, month }: YearMonth): CalendarCell[] {
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const totalDays = daysInMonth(year, month);

  const cells: CalendarCell[] = [];

  // Leading days from the previous month to fill the first week.
  if (firstWeekday > 0) {
    const prev = addMonths({ year, month }, -1);
    const prevTotal = daysInMonth(prev.year, prev.month);
    for (let i = firstWeekday - 1; i >= 0; i -= 1) {
      const day = prevTotal - i;
      cells.push({
        iso: isoFromParts(prev.year, prev.month, day),
        day,
        inMonth: false,
      });
    }
  }

  // The focused month's own days.
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push({ iso: isoFromParts(year, month, day), day, inMonth: true });
  }

  // Trailing days from the next month to complete the final week.
  const remainder = cells.length % 7;
  if (remainder > 0) {
    const next = addMonths({ year, month }, 1);
    const trailing = 7 - remainder;
    for (let day = 1; day <= trailing; day += 1) {
      cells.push({
        iso: isoFromParts(next.year, next.month, day),
        day,
        inMonth: false,
      });
    }
  }

  return cells;
}
